/* eslint-disable react/prop-types */
import React from "react";
import MediaQuery from "react-responsive";
import Expander from "../../../../components/Expander";
import { getVarsoUrl } from "../../../../common/helpers/helpers";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import showCanonicalLegend from "../../../../common/helpers/showCanonicalLegend";
import {
  allCodingLocationsNull,
  allSpliceDistancesNull,
  calculateLocationBrackets,
  renderTableHeader,
  renderFunctionalAnnotationNotice,
} from "./TranscriptsTemplate";
import {
  includeManePlus,
  includeManeSelect,
  includeAppris,
  includeUniprotId,
  isProteinLevelAvailable,
  showGeneColumn,
} from "./helpers";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";
import GeneWithRefGenomeLink from "../../../../common/helpers/GeneWithRefGenomeLink";
import { primaryColor1 } from "../../../../common/helpers/colors";
import ConditionalLink from "../../../../components/atoms/ConditionalLink/ConditonalLink";
import HelperText from "../../../../components/atoms/HelperText";
import ProteinLevel from "./ProteinLevel";
import Hgvs from "../Hgvs/Hgvs";

const RefseqTranscripts = ({
  schema,
  variantResult,
  varsomeHost,
  genomeName,
  isFLT3ITD,
}) => {
  const { refseq_transcripts: refseqTranscripts } = variantResult;

  if (schema && variantResult && refseqTranscripts) {
    const description = schema.properties.refseq_transcripts;
    const values = refseqTranscripts[0];
    const transcripts = values.items;

    if (transcripts) {
      const { acmg_annotation: acmgAnnotation } = variantResult;

      if (acmgAnnotation) {
        const { transcript } = variantResult.acmg_annotation;
        const annotationTranscript = transcript;
        const idx = transcripts.findIndex(
          (x) => x.name === annotationTranscript
        );

        if (idx !== -1) {
          const selectedTranscript = transcripts[idx];
          transcripts.splice(idx, 1);
          transcripts.splice(0, 0, selectedTranscript);
        }
      }
      const tmpl = (
        <>
          <div className="varsome-inner-container">
            <MediaQuery minWidth={768}>
              <table className="varsome-table">
                {renderTableHeader(
                  description.items.properties.items.items.properties,
                  variantResult,
                  transcripts
                )}
                <Expander showMore="4" component="tbody">
                  {transcripts.map((item, i) => {
                    return (
                      <tr
                        key={i}
                        className={
                          item.canonical ? "varsome-canonical-row" : ""
                        }
                      >
                        <td>
                          <ConditionalLink
                            href={`${varsomeHost}/transcript/${genomeName}/${item.name}`}
                            text={item.name}
                            color="red"
                          />
                          <br />
                          <a
                            href={getVarsoUrl(
                              `https://www.ncbi.nlm.nih.gov/nuccore/${item.name}`
                            )}
                            target="_blank"
                          >
                            <small style={{ color: primaryColor1 }}>
                              See on NCBI
                            </small>
                          </a>
                        </td>
                        <td>
                          {item.coding_impact}
                          <HelperText
                            fieldDescriptionKey={item.coding_impact}
                          />
                        </td>
                        {showGeneColumn(variantResult) && (
                          <td>
                            {item.gene_symbol && (
                              <GeneWithRefGenomeLink
                                href={`${varsomeHost}/gene/${item.gene_symbol}`}
                              >
                                {item.gene_symbol}
                              </GeneWithRefGenomeLink>
                            )}
                          </td>
                        )}
                        <td>
                          <Hgvs isFLT3ITD={isFLT3ITD} hgvs={item.hgvs} />
                        </td>
                        {isProteinLevelAvailable(transcripts) && (
                          <td>
                            <ProteinLevel isFLT3ITD={isFLT3ITD} item={item} />
                          </td>
                        )}
                        <td>
                          {item.location + calculateLocationBrackets(item)}
                        </td>
                        {!allCodingLocationsNull(transcripts) && (
                          <td>{item.coding_location}</td>
                        )}
                        {!allSpliceDistancesNull(transcripts) && (
                          <td>{item.splice_distance}</td>
                        )}
                        {includeManePlus(transcripts) && (
                          <td>{item?.mane_plus}</td>
                        )}
                        {includeManeSelect(transcripts) && (
                          <td>{item?.mane_select}</td>
                        )}
                        {includeAppris(transcripts) && (
                          <td>{item?.ensembl_appris}</td>
                        )}
                        {includeUniprotId(transcripts) && (
                          <td>
                            {item?.uniprot_id && (
                              <a
                                href={`https://www.uniprot.org/uniprotkb/${item.uniprot_id}/entry`}
                                target="_blank"
                              >
                                {item.uniprot_id}
                              </a>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </Expander>
              </table>
            </MediaQuery>
            <MediaQuery maxWidth={767}>
              <div>
                {transcripts.map((item, i) => {
                  return (
                    <dl className="dl-horizontal varsome-dl-horizontal" key={i}>
                      <dt className="col-3 col-xs-3">
                        {
                          description.items.properties.items.items.properties
                            .name.label
                        }
                      </dt>
                      <dd className="col-9 col-xs-9">
                        <a
                          href={`${varsomeHost}/transcript/${genomeName}/${item.name}`}
                          target="_blank"
                        >
                          {item.name}
                        </a>
                        <br />
                        <a
                          href={getVarsoUrl(
                            `https://www.ncbi.nlm.nih.gov/nuccore/${item.name}`
                          )}
                          target="_blank"
                        >
                          <small style={{ color: primaryColor1 }}>
                            See on NCBI
                          </small>
                        </a>
                      </dd>
                      <dt className="col-3 col-xs-3">
                        {
                          description.items.properties.items.items.properties
                            .coding_impact.label
                        }
                      </dt>
                      <dd className="col-9 col-xs-9">
                        {item.coding_impact}
                        <HelperText fieldDescriptionKey={item.coding_impact} />
                      </dd>
                      {showGeneColumn(variantResult) && (
                        <div>
                          <dt className="col-3 col-xs-3">Gene</dt>
                          <dd className="col-9 col-xs-9">
                            {item.gene_symbol && (
                              <GeneWithRefGenomeLink
                                href={`${varsomeHost}/gene/${item.gene_symbol}`}
                              >
                                {item.gene_symbol}
                              </GeneWithRefGenomeLink>
                            )}
                          </dd>
                        </div>
                      )}
                      <dt className="col-3 col-xs-3">
                        {
                          description.items.properties.items.items.properties
                            .hgvs.label
                        }
                      </dt>
                      <dd className="col-9 col-xs-9">
                        <Hgvs isFLT3ITD={isFLT3ITD} hgvs={item.hgvs} />
                      </dd>
                      {isProteinLevelAvailable(transcripts) && (
                        <div>
                          <dt className="col-3 col-xs-3">HGVS Protein</dt>
                          <dd className="col-9 col-xs-9">
                            <ProteinLevel isFLT3ITD={isFLT3ITD} item={item} />
                          </dd>
                        </div>
                      )}
                      <dt className="col-3 col-xs-3">
                        {
                          description.items.properties.items.items.properties
                            .location.label
                        }
                      </dt>
                      <dd className="col-9 col-xs-9">
                        {item.location + calculateLocationBrackets(item)}
                      </dd>
                      {!allCodingLocationsNull(transcripts) && (
                        <div>
                          <dt className="col-3 col-xs-3">
                            {
                              description.items.properties.items.items
                                .properties.coding_location.label
                            }
                          </dt>
                          <dd className="col-9 col-xs-9">
                            {item.coding_location}
                          </dd>
                        </div>
                      )}
                      {!allSpliceDistancesNull(transcripts) && (
                        <div>
                          <dt className="col-3 col-xs-3">
                            {
                              description.items.properties.items.items
                                .properties.splice_distance.label
                            }
                          </dt>
                          <dd className="col-9 col-xs-9">
                            {item.splice_distance}
                          </dd>
                        </div>
                      )}
                      {includeManePlus(transcripts) && (
                        <div>
                          <dt className="col-3 col-xs-3">
                            {
                              description.items.properties.items.items
                                .properties.mane_plus.label
                            }
                          </dt>
                          <dd className="col-9 col-xs-9">{item?.mane_plus}</dd>
                        </div>
                      )}
                      {includeManeSelect(transcripts) && (
                        <div>
                          <dt className="col-3 col-xs-3">
                            {
                              description.items.properties.items.items
                                .properties.mane_select.label
                            }
                          </dt>
                          <dd className="col-9 col-xs-9">
                            {item?.mane_select}
                          </dd>
                        </div>
                      )}
                      {includeAppris(transcripts) && (
                        <div>
                          <dt className="col-3 col-xs-3">
                            {
                              description.items.properties.items.items
                                .properties.ensembl_appris.label
                            }
                          </dt>
                          <dd className="col-9 col-xs-9">
                            {item?.ensembl_appris}
                          </dd>
                        </div>
                      )}
                      {includeUniprotId(transcripts) && (
                        <div>
                          <dt className="col-3 col-xs-3">
                            {
                              description.items.properties.items.items
                                .properties.uniprot_id.label
                            }
                          </dt>
                          <dd className="col-9 col-xs-9">
                            {item?.uniprot_id && (
                              <a
                                href={`https://www.uniprot.org/uniprotkb/${item.uniprot_id}/entry`}
                                target="_blank"
                              >
                                {item.uniprot_id}
                              </a>
                            )}
                          </dd>
                        </div>
                      )}
                    </dl>
                  );
                })}
              </div>
            </MediaQuery>
            {showCanonicalLegend(transcripts)}
          </div>
          {renderFunctionalAnnotationNotice()}
        </>
      );
      return (
        <div className="varsome-result">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              title="RefSeq transcripts"
              url={getVarsoUrl("http://www.ncbi.nlm.nih.gov/refseq/rsg/")}
              helperPopup={
                <HelperText fieldDescriptionKey="RefSeq transcripts" />
              }
              version={refseqTranscripts[0].version}
              content={tmpl}
            />
          </div>
        </div>
      );
    }
    return null;
  }

  return null;
};

RefseqTranscripts.propTypes = {};

RefseqTranscripts.defaultProps = {};

export default withErrorGuard(
  RefseqTranscripts,
  "RefseqTranscripts",
  "Refseq Transcripts"
);
